import { Link } from "react-router-dom";
import { ArrowRight, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface BlogCardProps {
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  publishedAt: string | null;
  categoryName?: string | null;
  authorName: string;
}

const BlogCard = ({ slug, title, excerpt, coverImageUrl, coverImageAlt, publishedAt, categoryName, authorName }: BlogCardProps) => {
  const { toast } = useToast();
  const formattedDate = publishedAt
    ? new Date(publishedAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/blog/${slug}`;
    if (navigator.share) {
      navigator.share({ title, text: excerpt || title, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast({ title: "Link copiado!", description: "Compartilhe com seus amigos." });
    }
  };

  return (
    <Link
      to={`/blog/${slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-card border border-border shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] transition-all duration-500 hover:shadow-[0_30px_80px_-10px_rgba(0,0,0,0.45)] hover:-translate-y-1"
    >
      {/* Share button */}
      <button
        onClick={handleShare}
        className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-card/80 text-muted-foreground shadow-md backdrop-blur-sm transition-all hover:bg-primary hover:text-primary-foreground"
        title="Compartilhar"
      >
        <Share2 className="h-3.5 w-3.5" />
      </button>

      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        {coverImageUrl ? (
          <img
            src={coverImageUrl}
            alt={coverImageAlt || title}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary/10 text-4xl text-primary/30">📝</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {categoryName && (
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-medium text-primary">
              {categoryName}
            </span>
          )}
          {formattedDate && <span>{formattedDate}</span>}
        </div>

        <h3 className="text-lg font-bold leading-snug text-foreground line-clamp-2 transition-colors duration-300 group-hover:text-primary">
          {title}
        </h3>

        {excerpt && (
          <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">
            {excerpt}
          </p>
        )}

        <div className="mt-auto flex items-center gap-1.5 pt-2 text-xs font-semibold uppercase tracking-widest text-primary transition-all duration-300 group-hover:gap-3">
          Ler artigo
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
